import { useState, useEffect, useCallback, useRef } from "react";
import appFetch from "../api/appFetch";
import { GetPlanningSettingsUrl } from "../constants/apiUrls";

/**
 * React hook for fetching planning settings for an outlet
 * @param {number} outletId - The outlet ID to fetch settings for
 * @returns {Object} - { canUseAgendasAndAbsencesInSettings, isLoading }
 */
export function usePlanningSettings(outletId) {
    const [canUseAgendasAndAbsencesInSettings, setCanUseAgendasAndAbsencesInSettings] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    
    // Track the current request to avoid stale updates
    const currentRequestIdRef = useRef(0);

    const fetchSettings = useCallback((id) => {
        if (!id) {
            setCanUseAgendasAndAbsencesInSettings(true);
            return;
        }

        // Increment request ID to track this specific request
        const requestId = ++currentRequestIdRef.current;
        
        setIsLoading(true);

        appFetch({
            url: GetPlanningSettingsUrl(id),
            onSuccess: (data) => {
                // Only update state if this is still the current request
                if (requestId === currentRequestIdRef.current) {
                    const canUse = data?.canUseAgendasAndAbsencesInSettings ?? true;
                    setCanUseAgendasAndAbsencesInSettings(canUse);
                }
            },
            onError: () => {
                // Only update state if this is still the current request
                if (requestId === currentRequestIdRef.current) {
                    setCanUseAgendasAndAbsencesInSettings(true);
                }
            },
            onFinalize: () => {
                // Only update state if this is still the current request
                if (requestId === currentRequestIdRef.current) {
                    setIsLoading(false);
                }
            }
        });
    }, []);

    useEffect(() => {
        fetchSettings(outletId);
    }, [outletId, fetchSettings]);

    return { 
        canUseAgendasAndAbsencesInSettings, 
        isLoading,
        refetchSettings: fetchSettings 
    };
}
